/**
 * ⌘K search palette (doc 147 §5.1, §9 Phase 1 · §5.3a PR G).
 *
 * An ARIA combobox over the fuse.js index. Arrow keys move the selection,
 * Enter opens the entity, Esc closes.
 *
 * Context-aware: when opened with an entity focused, prepends a
 * "For <Entity>" section of entity-scoped commands (Impact / Trace /
 * Patterns / etc.) sharing the same builder the dedicated entity
 * palette uses — so muscle memory transfers across the two surfaces.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { Icon } from '../components/Icon.js';
import { navigate, navigateToObjectKind } from '../state/hashRouter.js';
import type { SearchHit, SearchRecordKind } from '../search/searchRecords.js';
import { KIND_META } from '../state/objectKinds.js';
import { DomainBadge } from './DomainBadge.js';
import type { InsightIndex } from '../state/entityInsights.js';
import type { TrailEntry } from '../state/workflowTrail.js';
import {
  buildEntityCommands,
  filterCommands,
  type EntityCommand,
} from '../state/entityCommands.js';
import {
  paletteEmptyState,
  groupResults,
  SEARCH_KIND_ORDER,
  SEARCH_KIND_LABELS,
} from '../state/paletteSections.js';
import { searchRankPrompt } from '../state/aiPrompts.js';
import type { AiSuggestOptions } from '../state/aiPrompts.js';
import { parseRankedIds } from '../state/parseRankedIds.js';
import type { AskAtlasResult } from '../state/atlasApiTypes.js';
import { useFocusTrap } from '../state/useFocusTrap.js';

export interface SearchPaletteProps {
  /** Async query function from `useSearch`. */
  search: (query: string) => Promise<SearchHit[]>;
  ready: boolean;
  onClose: () => void;
  /** Entity id currently in focus across the app — drives the "For <X>" section. */
  entityContext?: string;
  /** Insight index for the focused entity's commands; omit to skip the section. */
  index?: InsightIndex;
  /** Workflow trail — drives the empty-query Recents section (PR Q5). */
  trail?: readonly TrailEntry[];
  /** Pinned entity ids — drives the empty-query Starred section (PR Q5). */
  pinned?: readonly string[];
  /** When provided together with `manifest`, enables AI re-ranking. */
  onAsk?: (opts: AiSuggestOptions) => Promise<AskAtlasResult>;
  /** Manifest reference for AI ranking; must be provided with `onAsk`. */
  manifest?: AtlasManifest;
}

const KIND_ICON: Record<SearchRecordKind, string> = {
  entity: KIND_META.entity.icon,
  // field is not an ObjectKind, only a SearchRecordKind; fields route to their owning entity with no independent KIND_META entry
  field: 'data_object',
  'firestore-path': KIND_META['firestore-path'].icon,
  surface: KIND_META.surface.icon,
  'cloud-function': KIND_META['cloud-function'].icon,
  'event-type': KIND_META['event-type'].icon,
  hub: KIND_META.hub.icon,
  feature: KIND_META.feature.icon,
  flag: KIND_META.flag.icon,
  statemachine: KIND_META.statemachine.icon,
};

/** Maximum entity commands surfaced inline; the rest are reachable via `e`. */
const MAX_CONTEXT_COMMANDS = 5;

type PaletteItem =
  | { kind: 'command'; command: EntityCommand }
  | { kind: 'hit'; hit: SearchHit };

export function SearchPalette({
  search,
  ready,
  onClose,
  entityContext,
  index,
  trail = [],
  pinned = [],
  onAsk,
  manifest,
}: SearchPaletteProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [selected, setSelected] = useState(0);
  const [aiRankedIds, setAiRankedIds] = useState<string[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const rankDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useFocusTrap(panelRef, true);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void search(query).then((results) => {
      if (cancelled) return;
      setHits(results);
      setSelected(0);
    });
    return () => {
      cancelled = true;
    };
  }, [query, search]);

  // Debounced AI ranking — fires 400 ms after the query settles.
  useEffect(() => {
    setAiRankedIds(null);
    if (rankDebounceRef.current) clearTimeout(rankDebounceRef.current);
    if (!onAsk || !manifest || query.length < 2) return;

    const entityHits = hits.filter((h) => h.record.kind === 'entity');
    if (entityHits.length === 0) return;

    const candidateIds = entityHits.map((h) => h.record.objectId);
    rankDebounceRef.current = setTimeout(() => {
      void onAsk(searchRankPrompt(query, candidateIds, manifest))
        .then((result) => {
          const ranked = parseRankedIds(result.answer, new Set(candidateIds));
          if (ranked.length > 0) setAiRankedIds(ranked);
        })
        .catch(() => {
          // Silent fallback — keep original Fuse.js order.
        });
    }, 400);

    return () => {
      if (rankDebounceRef.current) clearTimeout(rankDebounceRef.current);
    };
  }, [query, hits, onAsk, manifest]);

  // Apply AI ranking to entity hits; leave other hit kinds in original order.
  const displayHits = useMemo(() => {
    if (!aiRankedIds) return hits;
    const rankMap = new Map(aiRankedIds.map((id, i) => [id, i]));
    const entityH = hits.filter((h) => h.record.kind === 'entity');
    const otherH = hits.filter((h) => h.record.kind !== 'entity');
    const sorted = [...entityH].sort(
      (a, b) =>
        (rankMap.get(a.record.objectId) ?? Infinity) -
        (rankMap.get(b.record.objectId) ?? Infinity),
    );
    return [...sorted, ...otherH];
  }, [hits, aiRankedIds]);

  // Entity-scoped commands — only present when an entity is focused. Filtered
  // by the query so typing narrows both lists simultaneously.
  const allCommands = useMemo<EntityCommand[]>(
    () => (index && entityContext ? buildEntityCommands(index, entityContext) : []),
    [index, entityContext],
  );
  const contextCommands = useMemo<EntityCommand[]>(
    () => filterCommands(allCommands, query).slice(0, MAX_CONTEXT_COMMANDS),
    [allCommands, query],
  );

  // Flat list for keyboard nav — commands first, then global search hits.
  const items = useMemo<PaletteItem[]>(() => {
    const cmds: PaletteItem[] = contextCommands.map((c) => ({ kind: 'command', command: c }));
    const h: PaletteItem[] = displayHits.map((hit) => ({ kind: 'hit', hit }));
    return [...cmds, ...h];
  }, [contextCommands, displayHits]);

  // PR Q5 — empty-query sections (Recents · Starred) when nothing typed.
  const emptySections = useMemo(
    () => (query.trim() === '' ? paletteEmptyState(trail, pinned) : []),
    [query, trail, pinned],
  );
  // PR Q5 — grouped result buckets for active queries.
  const grouped = useMemo(
    () => groupResults(query.trim() !== '' ? displayHits : []),
    [query, displayHits],
  );

  // Keep the selection in range when the list shrinks.
  useEffect(() => {
    if (selected >= items.length) setSelected(0);
  }, [items.length, selected]);

  const activate = (item: PaletteItem | undefined): void => {
    if (!item) return;
    if (item.kind === 'command') {
      navigate(item.command.route);
    } else {
      navigateToObjectKind(item.hit.record.objectKind, item.hit.record.objectId);
    }
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(items[selected]);
    }
  };

  let itemIndex = 0;
  return (
    <div
      className="palette-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        className="search-palette palette"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Search the data model"
      >
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            className="palette-input"
            placeholder={ready ? 'Search entities, features, flags, and more…' : 'Building search index…'}
            value={query}
            disabled={!ready}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded={items.length > 0}
            aria-controls="palette-results"
            aria-autocomplete="list"
          />
          {aiRankedIds && (
            <span className="ai-ranked-chip" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--text2)' }}>
              ✦ AI-ranked
            </span>
          )}
        </div>
        <ul className="palette-results" id="palette-results" role="listbox">
          {/* PR Q5 — empty-query Recents + Starred when nothing typed. */}
          {emptySections.map((section) => (
            <div key={section.label}>
              <li className="palette-section-label" aria-hidden="true">
                {section.label}
              </li>
              {section.items.map((it) => (
                <li
                  key={`${section.label}-${it.key}`}
                  className="palette-row"
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    navigate({ kind: 'entity', id: it.key });
                    onClose();
                  }}
                >
                  <Icon name={section.label === 'STARRED' ? 'star' : 'history'} size="sm" />
                  <span className="palette-row-label">{it.label}</span>
                  {it.outcome && <span className="palette-row-sub">{it.outcome}</span>}
                  {it.shortcut && (
                    <kbd className="palette-row-kbd" aria-hidden="true">{it.shortcut}</kbd>
                  )}
                </li>
              ))}
            </div>
          ))}
          {contextCommands.length > 0 && (
            <li className="palette-section-label" aria-hidden="true">
              For {entityContext}
            </li>
          )}
          {contextCommands.map((c) => {
            const i = itemIndex++;
            return (
              <li
                key={`cmd-${c.id}`}
                className="palette-row palette-row-command"
                role="option"
                aria-selected={i === selected}
                onMouseEnter={() => setSelected(i)}
                onClick={() => activate({ kind: 'command', command: c })}
              >
                <Icon name="bolt" size="sm" />
                <span className="palette-row-label">{c.label}</span>
                <span className="palette-row-sub">{c.outcome}</span>
                {c.shortcut && (
                  <kbd className="palette-row-kbd" aria-hidden="true">
                    {c.shortcut}
                  </kbd>
                )}
              </li>
            );
          })}
          {items.length === 0 && query.trim().length >= 2 && (
            <li className="palette-row" style={{ color: 'var(--text3)' }}>
              No matches for “{query}”
            </li>
          )}
          {/* PR Q5 — grouped result sections, one per catalogued object kind + fields. */}
          {SEARCH_KIND_ORDER.map((kind) => {
            const bucket = grouped[kind];
            if (bucket.length === 0) return null;
            return (
              <div key={kind}>
                <li className="palette-section-label" aria-hidden="true">
                  {SEARCH_KIND_LABELS[kind]} · {bucket.length}
                </li>
                {bucket.map((hit) => {
                  const i = itemIndex++;
                  return (
                    <li
                      key={hit.record.id}
                      className="palette-row"
                      role="option"
                      aria-selected={i === selected}
                      onMouseEnter={() => setSelected(i)}
                      onClick={() => activate({ kind: 'hit', hit })}
                    >
                      <Icon name={KIND_ICON[hit.record.kind]} size="sm" />
                      <span className="palette-row-label">{hit.record.label}</span>
                      {hit.record.domain && <DomainBadge domainId={hit.record.domain} size="sm" />}
                      <span className="palette-row-sub">{hit.record.sublabel}</span>
                    </li>
                  );
                })}
              </div>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
